import { useAnonymousIdentifier } from "@/composables/anonymousIdentifier.client";
import { useUser } from "@/composables/user";
import { AiIdentification_Schema, AiIdentificationSummary_Schema } from "@/services/aiIdentification/aiIdentificationSchema";
import type { AiIdentification } from "@/services/aiIdentification/aiIdentificationTypes";
import { ApiClient, TokenAdd } from "@/services/apiClient";
import type { CategoryId } from "@/services/collection/categories/categoriesSchema";
import type { Uuid } from "@/types";
import { WholeNumber_Schema } from "@/utilities/schema";
import { z } from "zod";

export const identify = async (categoryId: CategoryId, images: File[]): Promise<AiIdentification> => {
  const requestTimeout = 45000; // ms
  const data = new FormData();
  const url = "api-public/ai";
  data.append("categoryId", categoryId.toString());
  data.append("description", ""); // description was removed from FE, though not from API
  images.forEach((image) => data.append("images[]", image));

  const { isUserLoggedIn } = useUser();
  const { getIdentifier } = useAnonymousIdentifier();
  if (!isUserLoggedIn.value) {
    data.append("fingerprint", getIdentifier());
  }

  return ApiClient.postPublic<AiIdentification>(url, {}, data, TokenAdd.IfAvailable, requestTimeout).then(({ data }) => AiIdentification_Schema.parse(data));
};

const ApiResponse_HistoryList_Schema = z.object({
  totalCount: WholeNumber_Schema,
  items: z.array(AiIdentificationSummary_Schema),
});
type ApiResponse_HistoryList = z.infer<typeof ApiResponse_HistoryList_Schema>;

export const fetchHistoryList = async (page: number, perPage: number) => ApiClient.get<ApiResponse_HistoryList>("api/ai", { page, perPage }).then(({ data }) => ApiResponse_HistoryList_Schema.parse(data));

export const fetchItem = async (itemId: Uuid): Promise<AiIdentification> => {
  const url = "api-public/ai";
  return ApiClient.getPublic<AiIdentification>(url, { id: itemId }).then(({ data }) => AiIdentification_Schema.parse(data));
};
